import { QuestionCircleOutlined } from '@ant-design/icons'
import type { Home } from '@lmc/ts-common'
import { FormRadio, FormRadioGroup, InputAndSelectUnit } from '@lmc/ui-kit'
import { Heading, Text } from '@lmc/ui-kit/src/general'
import { Divider, Image, Switch } from 'antd'
import { getNominative } from 'common/utils'
import ExternalLink from 'components/icons/ExternalLink'
import useHomeType from 'hooks/useHomeType'
import useMixpanelEvent, { MixpanelEvents } from 'hooks/useMixpanelEvent'
import { NAMESPACE } from 'locale'
import { accessControlConstants } from 'locale/accessControl'
import { lowerCase } from 'lodash'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import AccessControlPopover from './AccessControlPopover'
import mobileImage from './images/phone-illustration-SUlist.png'
import RotaBasedAccessPopover from './RotaBasedAccessPopover'

interface AccessControlProps {
  showAllResidentsButton: boolean
  onShowAllResidentsButtonChange: (value: boolean) => void
  visitBasedAccessControl: boolean
  onVisitBasedAccessControlChange: (value: boolean) => void
  startAccessTimeUnit: string
  onStartAccessTimeValueChange: (value: number | undefined) => void
  startAccessTimeValue: number | undefined
  onStartAccessTimeUnitChange: (value: string) => void
  endAccessTimeUnit: string
  onEndAccessTimeValueChange: (value: number | undefined) => void
  endAccessTimeValue: number | undefined
  onEndAccessTimeUnitChange: (value: string) => void
}

export const AccessControl = ({
  showAllResidentsButton,
  onShowAllResidentsButtonChange,
  visitBasedAccessControl,
  onVisitBasedAccessControlChange,
  startAccessTimeUnit,
  onStartAccessTimeValueChange,
  startAccessTimeValue,
  onStartAccessTimeUnitChange,
  endAccessTimeUnit,
  onEndAccessTimeValueChange,
  endAccessTimeValue,
  onEndAccessTimeUnitChange
}: AccessControlProps) => {
  const { t } = useTranslation(NAMESPACE.ACCESS_CONTROL)

  const { hasRota } = useHomeType()

  const sendMixpanelEvent = useMixpanelEvent()

  const preferredPeopleNominative: string = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative || ''
  )
  const peopleNominative = getNominative(preferredPeopleNominative, 'Residents')

  const onAllResidentsButtonChange = (value: boolean) => {
    onShowAllResidentsButtonChange(value)
    onVisitBasedAccessControlChange(!value)
  }

  const handleHelpArticleClick = () => {
    sendMixpanelEvent(MixpanelEvents.ACCESS_CONTROL_HELP_ARTICLE_CLICKED, {
      accessControlHelpArticleClicked: true
    })
  }

  const timeUnits = [
    {
      label: 'minutes',
      value: 'minutes'
    },
    {
      label: 'hours',
      value: 'hours'
    }
  ]

  return (
    <Container>
      <AccessControlCard>
        <Header>
          <Title>
            <Heading level={3}>
              {t(accessControlConstants.TITLE_PREFERENCES, {
                nominative: peopleNominative
              })}
            </Heading>
            <AccessControlPopover>
              <StyledQuestionCircleOutlined />
            </AccessControlPopover>
          </Title>
          <LightText>
            {t(accessControlConstants.ACCESS_CONTROL_DESCRIPTION, {
              nominative: lowerCase(peopleNominative)
            })}
          </LightText>
          <StyledDivider />
          <InfoSection>
            <Text level={2} weight="medium">
              {t(accessControlConstants.PRO_PLAN_INFO, {
                nominative: peopleNominative
              })}
            </Text>
          </InfoSection>
          <FormSection>
            <LightText>
              {t(accessControlConstants.CHOSE_ACCESS_LEVEL)}
            </LightText>
            <FormRadioGroup
              value={showAllResidentsButton}
              onChange={(e) => onAllResidentsButtonChange(e.target.value)}
            >
              <FormRadio
                value={true}
                text={t(accessControlConstants.SHOW_ALL, {
                  nominative: peopleNominative
                })}
              />
              <FormRadio
                value={false}
                text={t(accessControlConstants.ONLY_ASSIGNED, {
                  nominative: peopleNominative
                })}
              />
            </FormRadioGroup>
          </FormSection>
          {hasRota && (
            <RotaSection>
              <StyledDivider />
              <RotaAccessControlCard>
                <Header>
                  <TitleRow>
                    <Title>
                      <Heading level={4}>
                        {t(accessControlConstants.ROTA_BASED_ACCESS)}
                      </Heading>
                      <RotaBasedAccessPopover>
                        <StyledQuestionCircleOutlined />
                      </RotaBasedAccessPopover>
                    </Title>
                    <Link
                      to="https://support.logmycare.co.uk/en/articles/11799123-rostering-access-control"
                      target="_blank"
                      rel="noreferrer"
                      onClick={handleHelpArticleClick}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <Text level={1.5}>
                        {t(accessControlConstants.HOW_DO_I_SET_THIS_UP)}
                      </Text>
                      <ExternalLink />
                    </Link>
                  </TitleRow>
                  <StyledDivider />
                </Header>
                <Text>
                  {t(accessControlConstants.ROTA_BASED_ACCESS_DESCRIPTION, {
                    nominative: lowerCase(peopleNominative)
                  })}
                </Text>
                <StyledSwitch
                  checked={visitBasedAccessControl}
                  onChange={onVisitBasedAccessControlChange}
                  disabled={showAllResidentsButton}
                />
                <InfoSection>
                  <Text level={2} weight="medium">
                    {t(accessControlConstants.ROTA_BASED_ACCESS_INFO, {
                      nominative: peopleNominative
                    })}
                  </Text>
                </InfoSection>
                {visitBasedAccessControl && (
                  <AccessTimeContainer>
                    <AccessTimeRow>
                      <AccessTimeLabel>
                        <Text>
                          {t(accessControlConstants.ACCESS_STARTS_LABEL)}
                        </Text>
                      </AccessTimeLabel>
                      <InputAndSelectUnit
                        unitOptions={timeUnits}
                        unit={startAccessTimeUnit}
                        onUnitChange={onStartAccessTimeUnitChange}
                        value={startAccessTimeValue}
                        onChange={onStartAccessTimeValueChange}
                        description={t(accessControlConstants.BEFORE_THE_VISIT)}
                        placeholder={t(accessControlConstants.TYPE_HERE)}
                        step={startAccessTimeUnit === 'hours' ? '0.01' : '1'}
                        min={startAccessTimeUnit === 'hours' ? 0.25 : 15}
                        max={startAccessTimeUnit === 'hours' ? 168 : 9999}
                      />
                    </AccessTimeRow>
                    <AccessTimeRow>
                      <AccessTimeLabel>
                        <Text>
                          {t(accessControlConstants.ACCESS_ENDS_LABEL)}
                        </Text>
                      </AccessTimeLabel>
                      <InputAndSelectUnit
                        unitOptions={timeUnits}
                        unit={endAccessTimeUnit}
                        onUnitChange={onEndAccessTimeUnitChange}
                        value={endAccessTimeValue}
                        onChange={onEndAccessTimeValueChange}
                        description={t(accessControlConstants.AFTER_THE_VISIT)}
                        placeholder={t(accessControlConstants.TYPE_HERE)}
                        step={endAccessTimeUnit === 'hours' ? '0.01' : '1'}
                        min={endAccessTimeUnit === 'hours' ? 0.25 : 15}
                        max={endAccessTimeUnit === 'hours' ? 168 : 9999}
                      />
                    </AccessTimeRow>
                  </AccessTimeContainer>
                )}
              </RotaAccessControlCard>
            </RotaSection>
          )}
        </Header>
      </AccessControlCard>
      <ImageContainer>
        <StyledImage src={mobileImage} alt="Mobile app" preview={false} />
        <OverlayText weight="medium">
          {t(accessControlConstants.MY_RESIDENTS, {
            nominative: peopleNominative
          })}
        </OverlayText>
      </ImageContainer>
    </Container>
  )
}

const AccessControlCard = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  color: ${({ theme }) => theme.color.neutral.color3};
  background-color: ${({ theme }) => theme.color.neutral.color11};
  padding: 24px;
  border-radius: 16px;
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`
const StyledDivider = styled(Divider)`
  margin: 0;
`
const LightText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
`
const StyledQuestionCircleOutlined = styled(QuestionCircleOutlined)`
  color: ${({ theme }) => theme.color.neutral.color4};
  font-size: 18px;
`
const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`
const InfoSection = styled.div`
  background-color: ${({ theme }) => theme.color.neutral.color10};
  color: ${({ theme }) => theme.color.neutral.color4};
  padding: 12px 20px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.color.neutral.color7};
`
const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`
const Container = styled.div`
  display: flex;
  gap: 50px;
`
const RotaAccessControlCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  color: ${({ theme }) => theme.color.neutral.color4};
  background-color: ${({ theme }) => theme.color.neutral.color10};
  padding: 24px;
  border-radius: 16px;
`
const RotaSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`
const StyledSwitch = styled(Switch)`
  width: 20px;
`
const ImageContainer = styled.div`
  width: 350px;
  flex-shrink: 0;
  position: relative;
`
const OverlayText = styled(Text)`
  color: ${({ theme }) => theme.palette.darkpurple};
  position: absolute;
  top: 84px;
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
  z-index: 1;
  white-space: nowrap;
`
const StyledImage = styled(Image)`
  width: 100%;
  height: auto;
`
const AccessTimeRow = styled.div`
  display: flex;
  justify-content: space-between;
`
const AccessTimeLabel = styled.div`
  color: ${({ theme }) => theme.color.neutral.color4};
  margin-top: 10px;
`
const AccessTimeContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 85%;
`
const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
`
